import React, { useEffect, useRef } from 'react';
import { X, ShieldCheck } from 'lucide-react';
import { TERMS_DOCUMENT } from '../content/legalDocuments';

export type LegalDocumentKind = 'terms' | 'privacy';

/**
 * Bloco 38: Termos de Uso e Política de Privacidade abertos a partir da tela
 * de registro, antes de existir conta (sem login). O conteúdo é o mesmo do
 * Repositório de Documentos (content/legalDocuments.ts); "Política de
 * Privacidade (LGPD)" abre o documento na seção de privacidade e LGPD.
 */
export const LegalDocumentModal: React.FC<{ kind: LegalDocumentKind; onClose: () => void }> = ({ kind, onClose }) => {
  const privacyRef = useRef<HTMLElement | null>(null);
  const privacyHeading = TERMS_DOCUMENT.sections.find((s) => /PRIVACIDADE/i.test(s.heading))?.heading;

  useEffect(() => {
    if (kind === 'privacy') privacyRef.current?.scrollIntoView({ block: 'start' });
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [kind, onClose]);

  return (
    <div className="fixed inset-0 z-[120] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-slate-900 w-full max-w-3xl max-h-[90vh] rounded-[2.5rem] shadow-2xl border dark:border-slate-800 flex flex-col overflow-hidden">
        <div className="p-6 md:p-8 border-b dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-600 rounded-2xl text-white"><ShieldCheck size={22} /></div>
            <div>
              <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">
                {kind === 'privacy' ? 'Política de Privacidade (LGPD)' : 'Termos de Uso'}
              </h2>
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">{TERMS_DOCUMENT.title} · {TERMS_DOCUMENT.updatedAt}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-3 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500" aria-label="Fechar">
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 md:p-10 text-slate-700 dark:text-slate-300 custom-scrollbar">
          {TERMS_DOCUMENT.sections.map((section) => (
            <section key={section.heading} ref={section.heading === privacyHeading ? privacyRef : undefined} className="mb-8">
              <h4 className="font-black text-slate-900 dark:text-white uppercase tracking-wider border-b dark:border-slate-800 pb-2 mb-3">{section.heading}</h4>
              <div className="space-y-3 text-sm leading-relaxed">
                {section.paragraphs.map((paragraph, idx) => (
                  <p key={idx} className={/^[a-z]\)|^\d+\.\d+\./.test(paragraph) ? 'pl-4' : ''}>{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentModal;
